import type { GameState, Settings } from "@squire/core";
import type { DeviceSettings } from "../app/device.js";
import { APP_BUILD, describeBuild, type AvailableBuild } from "../app/update.js";
import { h } from "./dom.js";

export interface SettingsActions {
  saveGame(patch: Partial<Settings>): void;
  saveDevice(d: DeviceSettings): void;
  editLook(): void;
  exportProfile(): void;
  importProfile(file: File): void;
  openShop(): void;
  voices(): Promise<string[]>;
  testVoice(): void;
  testMic(): Promise<string>;
  checkUpdate(): Promise<AvailableBuild | null>;
  downloadUpdate(): Promise<boolean>;
  openOutbound(): void;
}

const row = (label: string, control: HTMLElement) => [h("label", { class: "pixel" }, label), control];

/** All SPEC 9 settings: game settings go to the server, device settings stay local. */
export function settingsView(s: GameState, device: DeviceSettings, act: SettingsActions): HTMLElement {
  const st = s.settings;
  const micInfo = h("p", { style: { fontSize: "0.85rem" } });
  const text = (v: string, max = 24) => h("input", { type: "text", value: v, maxlength: max });
  const time = (v: string) => h("input", { type: "time", value: v });
  const num = (v: number, min: number, max: number) =>
    h("input", { type: "number", value: String(v), min: String(min), max: String(max) });
  const check = (v: boolean) =>
    h("input", { type: "checkbox", checked: v, style: { width: "1.6em", height: "1.6em" } });

  const companionName = text(st.companionName);
  const userName = text(st.userName);
  const briefFrom = time(st.briefFrom);
  const briefUntil = time(st.briefUntil);
  const debriefFrom = time(st.debriefFrom);
  const activeFrom = time(st.activeFrom);
  const activeUntil = time(st.activeUntil);
  const eveningAt = time(st.eveningAt);
  const nightFrom = time(st.nightFrom);
  const nightUntil = time(st.nightUntil);
  const nudges = num(st.maxNudgesPerDay, 0, 3);
  const comeHome = num(st.comeHomeAfterHours, 0, 72);
  const workOn = check(st.workOn);
  const workFrom = time(st.workFrom);
  const workUntil = time(st.workUntil);

  const deviceName = text(device.name, 30);
  const profile = h(
    "select",
    {},
    h("option", { value: "wall", selected: device.profile === "wall" }, "ściana (zagaduje)"),
    h("option", { value: "pocket", selected: device.profile === "pocket" }, "kieszeń (milczy)"),
  );
  const voiceOn = check(device.voiceOn);
  const voiceSel = h("select", {}, h("option", { value: "" }, "domyślny polski"));
  void act.voices().then((names) => {
    for (const n of names) voiceSel.append(h("option", { value: n, selected: n === device.voiceName }, n));
  });
  const cameraOn = check(device.cameraOn);
  const cloudStt = check(device.cloudStt);
  const debug = check(device.debug);
  const nightDim = check(device.nightDim);
  const serverUrl = text(device.serverUrl, 200);
  const token = h("input", { type: "password", value: device.token, maxlength: 200, autocomplete: "off" });
  const file = h("input", { type: "file", accept: "application/json", style: { display: "none" } });
  file.addEventListener("change", () => {
    const f = file.files?.[0];
    if (f) act.importProfile(f);
  });

  const save = () => {
    act.saveGame({
      companionName: companionName.value.trim() || st.companionName,
      userName: userName.value.trim(),
      briefFrom: briefFrom.value,
      briefUntil: briefUntil.value,
      debriefFrom: debriefFrom.value,
      activeFrom: activeFrom.value,
      activeUntil: activeUntil.value,
      eveningAt: eveningAt.value,
      nightFrom: nightFrom.value,
      nightUntil: nightUntil.value,
      maxNudgesPerDay: Number(nudges.value),
      comeHomeAfterHours: Number(comeHome.value),
      workOn: workOn.checked,
      workFrom: workFrom.value,
      workUntil: workUntil.value,
    });
    act.saveDevice({
      ...device,
      name: deviceName.value.trim() || device.name,
      profile: profile.value === "pocket" ? "pocket" : "wall",
      voiceOn: voiceOn.checked,
      voiceName: voiceSel.value,
      cameraOn: cameraOn.checked,
      cloudStt: cloudStt.checked,
      debug: debug.checked,
      nightDim: nightDim.checked,
      serverUrl: serverUrl.value.trim(),
      token: token.value.trim(),
    });
  };

  return h(
    "div",
    {},
    h("h3", {}, "Postać"),
    h("div", { class: "grid2" }, ...row("Kompan", companionName), ...row("Ty", userName)),
    h(
      "div",
      { style: { display: "flex", gap: "0.6em", margin: "0.6em 0" } },
      h("button", { onclick: act.editLook }, "Wygląd"),
      h("button", { onclick: act.openShop }, "Sklep"),
    ),
    h("h3", { style: { marginTop: "1em" } }, "Rozmowy"),
    h(
      "div",
      { class: "grid2" },
      ...row("Brief od", briefFrom),
      ...row("Brief do", briefUntil),
      ...row("Debrief od", debriefFrom),
      ...row("Aktywne od", activeFrom),
      ...row("Aktywne do", activeUntil),
      ...row("„Wieczorem” to", eveningAt),
      ...row("Zaczepek dziennie (0–3)", nudges),
    ),
    h("h3", { style: { marginTop: "1em" } }, "To urządzenie"),
    h(
      "div",
      { class: "grid2" },
      ...row("Nazwa", deviceName),
      ...row("Profil", profile),
      ...row("Głos", voiceOn),
      ...row("Wybór głosu", voiceSel),
      ...row("Mowa przez mój Cloudflare", cloudStt),
      ...row("Tryb debug (w czacie /pomoc)", debug),
      ...row("Kamera (obecność)", cameraOn),
      ...row("Tryb nocny", nightDim),
      ...row("Noc od", nightFrom),
      ...row("Noc do", nightUntil),
      ...row("Wracaj do domu po (h, 0 = nie)", comeHome),
      ...row("Kompan chodzi do pracy (pn–pt)", workOn),
      ...row("Praca od", workFrom),
      ...row("Praca do", workUntil),
    ),
    h(
      "p",
      { style: { fontSize: "0.85rem" } },
      "Kamera porównuje tylko maleńkie klatki 32×24 na urządzeniu. Nic nie jest wysyłane ani zapisywane. " +
        "Mowa domyślnie rozpoznawana jest na urządzeniu. „Mowa przez mój Cloudflare” wysyła każdą wypowiedź " +
        "do Whispera na twoim Workerze, tylko do zamiany na tekst: nagranie nie jest nigdzie zapisywane.",
    ),
    h(
      "div",
      { style: { display: "flex", gap: "0.6em", flexWrap: "wrap" } },
      h("button", { onclick: act.testVoice }, "Sprawdź głos"),
      h(
        "button",
        {
          onclick: () => {
            micInfo.textContent = "Sprawdzam…";
            void act.testMic().then((t) => (micInfo.textContent = t));
          },
        },
        "Sprawdź mikrofon",
      ),
      h("button", { onclick: act.openOutbound }, "Co wysłałem"),
    ),
    micInfo,
    h("h3", { style: { marginTop: "1em" } }, "Aktualizacja"),
    updateView(act),
    h("h3", { style: { marginTop: "1em" } }, "Serwer"),
    h("div", { class: "grid2" }, ...row("Adres", serverUrl), ...row("Token", token)),
    h("h3", { style: { marginTop: "1em" } }, "Profil"),
    h(
      "div",
      { style: { display: "flex", gap: "0.6em", flexWrap: "wrap" } },
      h("button", { onclick: act.exportProfile }, "Eksportuj profil"),
      h("button", { onclick: () => file.click() }, "Importuj profil"),
      file,
    ),
    h(
      "div",
      { style: { display: "flex", justifyContent: "flex-end", marginTop: "1.2em" } },
      h("button", { class: "primary", onclick: save }, "Zapisz"),
    ),
  );
}

function updateView(act: SettingsActions): HTMLElement {
  const info = h("p", { style: { fontSize: "0.85rem" } }, `Ta wersja: build ${APP_BUILD || "lokalny"}.`);
  const get = h("button", { class: "primary hidden" }, "Pobierz i zainstaluj");
  get.onclick = () => {
    info.textContent = "Otwieram pobieranie… Po pobraniu stuknij powiadomienie i Zainstaluj.";
    void act.downloadUpdate().then((ok) => {
      if (!ok) info.textContent = "Nie udało się pobrać linku. Sprawdź połączenie.";
    });
  };
  const check = h("button", {}, "Sprawdź aktualizację");
  check.onclick = () => {
    info.textContent = "Sprawdzam…";
    void act.checkUpdate().then((b) => {
      info.textContent = b
        ? `Dostępna: ${describeBuild(b)}. Ta wersja: build ${APP_BUILD || "lokalny"}.`
        : `Masz najnowszą wersję (build ${APP_BUILD || "lokalny"}).`;
      get.classList.toggle("hidden", !b);
    });
  };
  return h(
    "div",
    {},
    info,
    h("div", { style: { display: "flex", gap: "0.6em", flexWrap: "wrap" } }, check, get),
  );
}

/** First run without a server configured: token (or QR link) or a local demo. */
export function connectView(
  device: DeviceSettings,
  onConnect: (url: string, token: string) => void,
  onLocal: () => void,
): HTMLElement {
  const url = h("input", {
    type: "text",
    value: device.serverUrl,
    placeholder: "https://squire.twoja-nazwa.workers.dev",
    style: { width: "100%" },
  });
  const token = h("input", {
    type: "password",
    value: device.token,
    placeholder: "SQUIRE_TOKEN",
    style: { width: "100%" },
  });
  return h(
    "div",
    {},
    h(
      "p",
      {},
      "Podaj adres serwera i token. Możesz też otworzyć link z tokenem (np. z kodu QR): adres?server=…&token=…",
    ),
    h(
      "div",
      { class: "grid2", style: { gridTemplateColumns: "auto 1fr" } },
      ...row("Serwer", url),
      ...row("Token", token),
    ),
    h(
      "div",
      {
        style: {
          display: "flex",
          gap: "0.8em",
          justifyContent: "flex-end",
          marginTop: "1.2em",
          flexWrap: "wrap",
        },
      },
      h("button", { class: "ghost", onclick: onLocal }, "Tryb demo bez serwera"),
      h(
        "button",
        { class: "primary", onclick: () => onConnect(url.value.trim(), token.value.trim()) },
        "Połącz",
      ),
    ),
  );
}
